import { useEffect } from "react";
import { Image } from "react-native";
import type { ImageStyle, StyleProp } from "react-native";
import Animated, {
  Easing,
  useAnimatedStyle,
  useSharedValue,
  withDelay,
  withTiming,
} from "react-native-reanimated";
import { useReducedMotion } from "../hooks/useReducedMotion";

type Props = {
  /** require()'d image source for the 3D hero icon. */
  source: number;
  /** Width in pixels. Defaults to 80. */
  width?: number;
  /** Height in pixels. Defaults to 96. */
  height?: number;
  style?: StyleProp<ImageStyle>;
  /** Test handle (surfaces as data-testid on web); the image has no accessible name. */
  testID?: string;
};

export function DialogIcon({
  source,
  width = 80,
  height = 96,
  style,
  testID,
}: Props) {
  const reducedMotion = useReducedMotion();
  const rotate = useSharedValue(reducedMotion ? 0 : -8);
  const opacity = useSharedValue(reducedMotion ? 1 : 0);

  useEffect(() => {
    // Native resolves the preference after the first render, so the settled
    // pose has to be applied here too, not only in the initial values.
    if (reducedMotion) {
      rotate.value = 0;
      opacity.value = 1;
      return;
    }
    rotate.value = withDelay(
      180,
      withTiming(0, { duration: 720, easing: Easing.out(Easing.cubic) }),
    );
    opacity.value = withDelay(
      180,
      withTiming(1, { duration: 360, easing: Easing.out(Easing.ease) }),
    );
  }, [rotate, opacity, reducedMotion]);

  const animatedStyle = useAnimatedStyle(() => ({
    transform: [{ rotate: `${rotate.value}deg` }],
    opacity: opacity.value,
  }));

  // A settled icon is a still image, so it renders as a plain one. An
  // Animated.Image keeps writing a transform even once the rotation has
  // landed on zero, and a transformed element is composited on the GPU, where
  // the artwork's soft edges resolve differently from one paint to the next.
  const Picture = reducedMotion ? Image : Animated.Image;

  return (
    <Picture
      source={source}
      testID={testID}
      style={[
        { width, height, alignSelf: "center" },
        reducedMotion ? null : animatedStyle,
        style,
      ]}
      resizeMode="contain"
    />
  );
}
